import { LazyIssueApp } from '@jira/issue__lazy-app/src/LazyIssueApp.tsx';
import { currentUserResource } from '@jira/router-resources__current-user/src/useCurrentUserResource.tsx';
import { aiEntitlementResource } from '@jira/router-resources__ai-entitlement/src/aiEntitlementResource.tsx';
import {
	getRapidboardResources,
	getRapidBoardDataResource,
	getUIFBacklogDataResource,
} from '@jira/router-resources__classic-projects/src/getRapidboardResources.tsx';
import { getEditModelResource } from '@jira/router-resources__classic-projects/src/getEditModelResource.tsx';
import { getBoardDetailsResource } from '@jira/router-resources__classic-projects/src/getBoardDetailsResource.tsx';
import { backlogCMPViewSettingsResource } from '@jira/router-resources__classic-projects-uif-backlog/src/uifBacklogResource.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { classicSoftwareBacklogEmbedRoute } from '@jira/router-routes__software-classic-backlog-routes/src/classicSoftwareBacklogEmbedRoute.tsx';
import { chromelessLayout } from '@jira/platform__layouts-chromeless-layout/src/index.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import {
	forgeBacklogModuleResource,
	forgeSprintModuleResource,
} from '@jira/navigation-apps__resources/src/controllers/forge/index.tsx';
import { epicsLiteResource } from '@jira/router-resources__next-gen-backlog/src/epicsLiteResource.tsx';
import { backlogFilterRefinementResource } from '@jira/router-resources__next-gen-backlog/src/backlogFilterRefinementResource.tsx';
import { permissionsResource } from '@jira/router-resources__next-gen-backlog/src/permissionsResource.tsx';
import { confluenceUserPermissionsResource } from '@jira/router-resources__confluence-user-permissions/src/confluenceUserPermissionsResource.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { jiraSpaResources } from '@jira/router-resources__spa-page-container/src/jiraSpaResources.tsx';
import { AGGRESSIVE_EARLY_ASSET_STRATEGY } from '@jira/platform__common-constants/src/ssr-early-assets.tsx';
import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { Route } from '@atlassian/react-resource-router';
import { classicBacklogCapability } from './common/constants.tsx';
import {
	classicBacklogPageEntryPoint,
	GSNClassicBacklogPageEntryPoint,
} from './ui/page-entry-point/index.tsx';

export const classicSoftwareBacklogEmbedRouteEntry: Route = createEntry(
	classicSoftwareBacklogEmbedRoute,
	() => ({
		group: 'classic-software',
		ufoName: 'classic-backlog-embed',
		layout: chromelessLayout,
		resources: [
			...getUIFBacklogDataResource(),
			...getRapidboardResources(),
			...getNavigationResources(),
			...getIssueViewResources(),
			// eslint-disable-next-line jira/router/no-project-context-resource
			projectContextResource,
			getRapidBoardDataResource(),
			getEditModelResource(),
			getBoardDetailsResource(),
			forgeBacklogModuleResource,
			forgeSprintModuleResource,
			...jiraSpaResources(),
			aiEntitlementResource,
			resourceWithCondition(
				() => !__SERVER__ || fg('enable_ssr_for_cmp_backlog'),
				epicsLiteResource,
			),
			backlogCMPViewSettingsResource,
			resourceWithCondition(() => fg('jira_backlog_agg_filters'), backlogFilterRefinementResource),
			currentUserResource,
			permissionsResource,
			resourceWithCondition(
				() => fg('twcg-992-loom-meeting-notes-sprint-planning-gate'),
				confluenceUserPermissionsResource,
			),
		],
		afterPaint: [LazyIssueApp],
		entryPoint: () =>
			fg('navx-global-side-nav') ? GSNClassicBacklogPageEntryPoint : classicBacklogPageEntryPoint,
		preloadOptions: {
			earlyChunks: ['software-cmp-backlog-early-entry'],
		},
		meta: {
			capability: classicBacklogCapability,
		},
		earlyAssets: AGGRESSIVE_EARLY_ASSET_STRATEGY,
		bifrost: {
			bifrostGroup: 'shell-only',
		},
		excludeFromWrmDeferredLoading: true,
		isStreamingEnabled: fg('jfp-magma-ssr-streaming-enable-backlog'),
	}),
);
